// eslint-disable-next-line no-unused-vars
import { h, createRef } from 'preact';
import PropTypes from 'prop-types';
import { format, parse, getDate, getMonth, getYear } from 'date-fns';
import { __ } from '@wordpress/i18n';

// Components
import { DatePicker } from './DatePicker';

const DATE_FORMAT = /(?:19|20)[0-9]{2}-(?:(?:0[1-9]|1[0-2])-(?:0[1-9]|1[0-9]|2[0-9])|(?:(?!02)(?:0[1-9]|1[0-2])-(?:30))|(?:(?:0[13578]|1[02])-31))/;

const dateAdapter = {
	/**
	 * Parse the date.
	 *
	 * @param {string} value Value from input.
	 * @param {function} createDate Function to call when the date parts have been retrieved.
	 * @return {object} The date object.
	 */
	parse( value = '', createDate ) {

		const matches = value.match( DATE_FORMAT );

		if ( !matches ) {
			return;
		}

		const date = parse( value, 'yyyy-MM-dd', new Date() );
		const day = getDate( date );
		const month = getMonth( date );
		const year = getYear( date );

		return createDate( day, month, year );
	},
	/**
	 * Format the date.
	 * @param {object} date Date object.
	 * @return {string}
	 */
	format( date ) {
		return format( date, 'yyyy-MM-dd' );
	},
};

/**
 * The main component.
 *
 * @param {Object} props Props
 * @return {Object} JSX
 */
const DateInput = ( { group, name, slug, description, disabled, value, update, className } ) => {
	const uniqueId = `field-${group}-${slug}`.toLowerCase().replace( ' ', '_' );
	/**
	 * Handle on change.
	 *
	 * @param {object} event The event object.
	 */
	const handleOnChange = ( event ) => {
		const { target, detail } = event;
		const targetInput = target.querySelector( 'input' );

		update(event);

		// Bail early.
		if ( !targetInput.id.includes( 'from' ) ) {
			return;
		}

		const fieldset = target.closest( 'fieldset' );
		const datePickers = Array.from( fieldset.querySelectorAll( 'duet-date-picker' ) );
		const [endDatePicker] = datePickers.filter( ( picker ) => picker !== target );

		endDatePicker.setAttribute( 'min', detail.value );
	};

	/**
	 * Set "selected" attribute to options in a select field.
	 */
	const handleSelectChange = ( select ) => {
		if ( !select ) {
			return;
		}

		const selectedText = select.value;
		for ( let i = 0; i < select.options.length; i++ ) {
			select.options[i].removeAttribute( 'selected' );
			if ( select.options[i].value === selectedText ) {
				select.options[i].setAttribute( 'selected', '' );
			}
		}
	};

	/**
	 * Handle open
	 *
	 * @param {Event} event The select change
	 */
	const handleOpen = ( event ) => {
		const { target } = event;

		const year = target.querySelector( '.duet-date__select--year' );
		const month = target.querySelector( '.duet-date__select--month' );

		handleSelectChange( year );
		handleSelectChange( month );

		year.addEventListener( 'change', () => {
			handleSelectChange( year );
		} );

		month.addEventListener( 'change', () => {
			handleSelectChange( month );
		} );
	};

	return (
		<div className={className}>
			<label htmlFor={uniqueId}>
				{name}
			</label>
			<DatePicker
				ariaDescribedby={`${uniqueId}-description`}
				dateAdapter={dateAdapter}
				identifier={uniqueId}
				localization={window.MSRSearch.i18n.duetDatePicker}
				name={slug}
				disabled={disabled}
				value={value}
				onChange={handleOnChange}
				onOpen={handleOpen}
			/>
			<span id={`${uniqueId}-description`} className="sr-only">
				{description}
			</span>
			<span className="duet-date__input-feedback font-weight-bold mt-2">{__('Invalid date', 'ms-research')}</span>
		</div>
	);
};

DateInput.defaultProps = {
	description: '',
	disabled: false,
	value: '',
	update: () => {}
};

DateInput.propTypes = {
	description: PropTypes.string,
	disabled: PropTypes.bool,
	group: PropTypes.string.isRequired,
	name: PropTypes.string.isRequired,
	slug: PropTypes.string.isRequired,
	value: PropTypes.string,
	update: PropTypes.func
};

export default DateInput;
